import { DateValue } from '@ds';
import {
  DateString,
  Location,
  PaginationSearchParams,
  SearchQuery,
  SortOrderQuery,
  UseQueryConfig,
  Uuid,
} from '@/api/types';
import { AircraftAvailabilityView, EmployeeComposed, EmployeeRole, OcsConsoleData } from '@/api/apis/resources';
import { TMDXFile } from '@/api/apis/files';
import { RouteTypeEnum } from '@/api/apis/route';
import { User, UserRole, UserType } from '../user';
import { ClinicalData, ClinicalMediaFile, NRPOption, NRPType, OffOcsType, OrganType } from './clinical-data.types';
import {
  CaseStatus,
  CaseStatusType,
  CustomerCaseStatus,
  DispositionReasonType,
  DispositionSpecificationType,
  OutcomeType,
  TurndownOutcome,
} from './status';
import { CustomerMT } from '@/api/apis/customer';
import { QuoteTotalType } from '@/api/apis/quote';
import { OrganAcceptanceCriteriaFields } from '../cases';
import { TrialProgram } from '../facilities/trial-programs';
import { ServiceRegionId } from '@/constants/serviceRegion.constants';
import { DonorData, SplitMetadata } from '@/api/apis/case/opo-case';
import { OpoOrganizationContact } from '@/api/apis/opo';

export enum CaseOrigin {
  Tmdx = 'Tmdx',
  Customer = 'Customer',
  Opo = 'Opo',
}

export type CaseFile = TMDXFile & {
  addedFromPlatform?: CaseOrigin;
};

export enum DonorType {
  DBD = 'DBD',
  DCD = 'DCD',
}

export enum RoutePlanStatus {
  RoutePlanningStart = 'RoutePlanning',
  WaitingForAviationApproval = 'WaitingForAviationApproval',
  WaitingForQuoteApproval = 'WaitingForQuoteApproval',
  RoutePlanningDone = 'RoutePlanningDone',
  PendingResourcesBooking = 'PendingResourcesBooking',
  ResourcesBooked = 'ResourcesBooked',
}

export enum GroundConfirmation {
  Confirmed = 'Confirmed',
  Pending = 'Pending',
}

export enum QuoteStatus {
  NoQuote = 'NoQuote',
  Created = 'Created',
  ApprovedInternally = 'ApprovedInternally',
  SentToTXCenter = 'SentToTxCenter',
  Recalled = 'Recalled',
  Rejected = 'Rejected',
  Approved = 'Approved',
  RejectedInternally = 'RejectedInternally', // not in API
  InvoiceSent = 'InvoiceSent', // not in API
  InvoicePaid = 'InvoicePaid', // not in API
}

export enum TransportType {
  Ground = 'Ground',
  Avia = 'Avia',
}

export enum ServiceType {
  NopWithTmdxSurgeon = 'NopWithTmdxSurgeon',
  NopWithoutTmdxSurgeon = 'NopWithoutTmdxSurgeon',
  DirectAcquisition = 'DirectAcquisition',
}

export enum LogisticsProviderType {
  None = 'None',
  TMDX = 'TMDX',
  SupplementalLiftProvider = 'SupplementalLiftProvider',
  TMDXAffiliate = 'TMDXAffiliate',
  TXCenterProvider = 'TxCenter',
  SelfServiced = 'SelfServiced',
  TEDS = 'Teds',
}

export enum CaseLogEventType {
  CaseDraftCreated = 'CaseDraftCreated',
  CaseClinicalScreeningCreated = 'CaseClinicalScreeningCreated',
  CaseTxCenterDraftCreated = 'CaseTxCenterDraftCreated',
  CaseDetailsUpdated = 'CaseDetailsUpdated',
  CaseRecipientHospitalORDataUpdated = 'CaseRecipientHospitalORDataUpdated',
  CaseCreated = 'CaseCreated',
  CaseDraftDeleted = 'CaseDraftDeleted',
  CaseStatusUpdated = 'CaseStatusUpdated',
  CaseRouteAdded = 'CaseRouteAdded',
  CaseResourceReserved = 'CaseResourceReserved',
  CaseResourceReleased = 'CaseResourceReleased',
  CaseResourceAssigned = 'CaseResourceAssigned',
  CaseResourceReassigned = 'CaseResourceReassigned',
  CaseResourceUnassigned = 'CaseResourceUnassigned',
  CaseResourceInvitationAccepted = 'CaseResourceInvitationAccepted',
  CaseResourceInvitationDeclined = 'CaseResourceInvitationDeclined',
  ResourceStatusUpdated = 'ResourceStatusUpdated',
  CaseQuoteRedacted = 'CaseQuoteRedacted',
  DonorORTimeDelayAdded = 'DonorORTimeDelayAdded',
  DonorORTimeDelayUpdated = 'DonorORTimeDelayUpdated',
  CaseNoteAdded = 'CaseNoteAdded',
  CaseNoteRemoved = 'CaseNoteRemoved',
  CaseNoteUpdated = 'CaseNoteUpdated',
  CustomerFeedbackAdded = 'CustomerFeedbackAdded',
  InvoiceCreated = 'InvoiceCreated',
  OrganAcceptanceCriteriaUpdated = 'OrganAcceptanceCriteriaUpdated',
  OrganAcceptanceCriteriaCleared = 'OrganAcceptanceCriteriaCleared',
  OrganAcceptanceCriteriaReset = 'OrganAcceptanceCriteriaReset',
  ReAllocatedCaseCreated = 'ReAllocatedCaseCreated',
  ReAllocatedCaseUpdated = 'ReAllocatedCaseUpdated',
  ReAllocatedCaseRemoved = 'ReAllocatedCaseRemoved',
  AttachFiles = 'AttachFiles',
  DetachFile = 'DetachFile',
  CaseReviewUpdated = 'CaseReviewUpdated',
  CaseQuoteCommentSet = 'CaseQuoteCommentSet',
  CaseQuoteCreated = 'CaseQuoteCreated',
  CaseQuoteUpdated = 'CaseQuoteUpdated',
  CaseQuoteRejectedInternally = 'CaseQuoteRejectedInternally',
  CaseQuoteApprovedInternally = 'CaseQuoteApprovedInternally',
  CaseQuoteSentToTxCenter = 'CaseQuoteSentToTxCenter',
  CaseQuoteRejectedByTxCenter = 'CaseQuoteRejectedByTxCenter',
  CaseQuoteApprovedByTxCenter = 'CaseQuoteApprovedByTxCenter',
  CaseQuoteApprovedTxCenterProvider = 'CaseQuoteApprovedTxCenterProvider',
  CaseQuoteRecalled = 'CaseQuoteRecalled',
  CaseQuoteDryRunDiscountExcluded = 'CaseQuoteDryRunDiscountExcluded',
  CustomRouteInitiated = 'CustomRouteInitiated',
  RoutePlanningStatusUpdated = 'RoutePlanningStatusUpdated',
  CaseResourceLogisticsStatusUpdated = 'CaseResourceLogisticsStatusUpdated',
  CaseRouteUpdated = 'CaseRouteUpdated',
  CaseRouteInitiated = 'CaseRouteInitiated',
  CaseDeclined = 'CaseDeclined',
  ServiceDeclined = 'ServiceDeclined',
  OpoContactsAssigned = 'OpoContactsAssigned',
  OpoContactIsPrimaryChanged = 'OpoContactIsPrimaryChanged',
  OpoContactsUnassigned = 'OpoContactsUnassigned',
  CaseResourceStartTimeEstablished = 'CaseResourceStartTimeEstablished',
  CaseLinkAdded = 'CaseLinkAdded',
  CaseLinkUpdated = 'CaseLinkUpdated',
  CaseLinkRemoved = 'CaseLinkRemoved',
  RequestOrganApproval = 'RequestOrganApproval',
  OrganApproved = 'OrganApproved',
  OrganDeclined = 'OrganDeclined',
  CreateSurgeonCaseSummary = 'CreateSurgeonCaseSummary',
  CaseInvoiceCreated = 'CaseInvoiceCreated',
  LightsSirensSupportChanged = 'LightsSirensSupportChanged',
  ServiceCompleted = 'ServiceCompleted',
  RouteCommentUpdated = 'RouteCommentUpdated',
  CaseDataSyncStatusChanged = 'CaseDataSyncStatusChanged',
  TransplantOutcomeTypeSet = 'TransplantOutcomeTypeSet',
  CaseSalesOrderUpdated = 'CaseSalesOrderUpdated',
  CaseAssignedEmployeePrimaryChanged = 'CaseAssignedEmployeePrimaryChanged',
  RandomizationResultSet = 'RandomizationResultSet',
  QuestionnaireResultSet = 'QuestionnaireResultSet',
  IdentityAccessTicketGranted = 'IdentityAccessTicketGranted',
  IdentityAccessTicketRevoked = 'IdentityAccessTicketRevoked',
  ResourceTransferredToCase = 'ResourceTransferredToCase',
  ReAllocationClinicalDataCopied = 'ReAllocationClinicalDataCopied',
  ReAllocationDonorDataCopied = 'ReAllocationDonorDataCopied',
  NotesCopied = 'NotesCopied',
  QuestionaryEditAllowanceChanged = 'QuestionaryEditAllowanceChanged',
  KidneyTrialChanged = 'KidneyTrialChanged',
  KidneyRandomizationResultSet = 'KidneyRandomizationResultSet',
  KidneyRandomizationResultRemoved = 'KidneyRandomizationResultRemoved',
  CaseResourceLabelsChanged = 'CaseResourceLabelsChanged',
  CaseOpoDraftCreated = 'CaseOpoDraftCreated',
  DonorDataUpdated = 'DonorDataUpdated',
  DonorDataReset = 'DonorDataReset',
  CaseSplitIntoOrgans = 'CaseSplitIntoOrgans',
  CaseInitiatedFromAnotherDraft = 'CaseInitiatedFromAnotherDraft',
  // TODO Investigate structure of these logs
  // RouteLegsUpdated = 'RouteLegsUpdated',
  // UpdateSurgeonCaseSummary = 'UpdateSurgeonCaseSummary',
  // MaoTripsCreated = 'MaoTripsCreated',
  // MaoTripsCreationFailed = 'MaoTripsCreationFailed',
  // ReAllocationClinicalDataCopied = 'ReAllocationClinicalDataCopied',
}

export enum CaseLogGroup {
  CaseFeedback = 'CaseFeedback',
  CaseLogistics = 'CaseLogistics',
  CaseManagement = 'CaseManagement',
  CaseUpdate = 'CaseUpdate',
  FinancialManagement = 'FinancialManagement',
  ResourceManagement = 'ResourceManagement',
  AchieveCaseAccess = 'AchieveCaseAccess',
  Unknown = 'Unknown', // Client only
}

export enum CaseSalesOrderStatus {
  Error = 'Error',
  Success = 'Success',
  Sent = 'Sent',
  Reprocess = 'Reprocess',
}

export enum CaseUserGroup {
  CaseCreator = 'CaseCreator',
  LogisticsManagers = 'LogisticsManagers',
  ClinicalCoordinators = 'ClinicalCoordinators',
  ClinicalLeads = 'ClinicalLeads',
  AviationSpecialists = 'AviationSpecialists',
  GroundSpecialists = 'GroundSpecialists',
  SrLogisticsManagers = 'SrLogisticsManagers',
  QuoteApprovers = 'QuoteApprovers',
  OrganLeadership = 'OrganLeadership',
  OrganManagement = 'OrganManagement',
  Customers = 'Customers',
  CustomerAdministrators = 'CustomerAdministrators',
  CustomerCoordinators = 'CustomerCoordinators',
  CustomerTransplantSupporters = 'CustomerTransplantSupporters',
  CustomerReceivingSurgeons = 'CustomerReceivingSurgeons',
  OrganManagementSpecialists = 'OrganManagementSpecialists',
}

export enum EmployeeLabels {
  Primary = 'Primary',
  Relief = 'Relief',
  Proctor = 'Proctor',
  Shadow = 'Shadow',
}

export const CaseUserGroupNames = {
  [CaseUserGroup.CaseCreator]: 'Case Creator',
  [CaseUserGroup.LogisticsManagers]: 'Logistics Managers',
  [CaseUserGroup.ClinicalCoordinators]: 'Clinical Coordinators',
  [CaseUserGroup.ClinicalLeads]: 'Clinical Leads',
  [CaseUserGroup.AviationSpecialists]: 'Aviation Specialists',
  [CaseUserGroup.GroundSpecialists]: 'Ground Specialists',
  [CaseUserGroup.SrLogisticsManagers]: 'Sr. Logistics Managers',
  [CaseUserGroup.QuoteApprovers]: 'Quote Approvers',
  [CaseUserGroup.OrganLeadership]: 'Organ Leadership',
  [CaseUserGroup.OrganManagement]: 'Organ Management',
  [CaseUserGroup.Customers]: 'Customers',
  [CaseUserGroup.CustomerAdministrators]: 'Customer Administrators',
  [CaseUserGroup.CustomerCoordinators]: 'Customer Coordinators',
  [CaseUserGroup.CustomerTransplantSupporters]: 'Customer Transplant Supporters',
  [CaseUserGroup.CustomerReceivingSurgeons]: 'Customer Receiving Surgeons',
  [CaseUserGroup.OrganManagementSpecialists]: 'Organ Management Specialists',
};

export type CaseNote = {
  noteId: Uuid;
  message: string;
  userId: Uuid;
  createdAt: DateString;
  modifiedAt: DateString;
  isPinned: boolean;
  user?: User;
};

export enum InvoiceState {
  NoInvoice = 'NoInvoice',
  InvoiceAdded = 'InvoiceAdded',
}

export enum InvoiceStatus {
  Open = 'Open',
  PartiallyCredited = 'PartiallyCredited',
  FullyCredited = 'FullyCredited',
  PartiallyPaid = 'PartiallyPaid',
  Paid = 'Paid',
}

export type Invoice = {
  id: Uuid;
  fileName: string;
  status: InvoiceStatus;
};

export enum ResourceLogisticsStatus {
  None = 'None',
  Reserved = 'Reserved',
  BookingSent = 'BookingSent',
  Booked = 'Booked',
  DeployedToDonorHospital = 'DeployedToDonorHospital',
  AtDonorHospital = 'AtDonorHospital',
  DeployedFromDonorHospital = 'DeployedFromDonorHospital',
  AtTXCenter = 'AtTXCenter',
  DeployedFromTXCenter = 'DeployedFromTXCenter',
  DeployedToHub = 'DeployedToHub',
  OCSDeployed = 'OCSDeployed',
  OCSDone = 'OCSDone',
  Done = 'Done',
  RecoveryIsCompleted = 'RecoveryIsCompleted',
  BookedInMao = 'BookedInMao',
  CancelledInMao = 'CancelledInMao',
  // Exists for backwards compatibility
  Available = 'Available', // Not in API
  DeployedToTX = 'DeployedToTX', // Not in API
  ReturningToBase = 'ReturningToBase', // Not in API
  OCSOnCase = 'OCSOnCase', // Not in API
  OnRest = 'OnRest', // Not in API
  OnCase = 'OnCase', // Not in API
}

export enum CaseResourceType {
  Employee = 'Employee',
  OCSConsole = 'OCSConsole',
  Aircraft = 'Aircraft',
  ClinicalSpecialist = 'ClinicalSpecialist',
  Surgeon = 'Surgeon',
  Crew = 'Crew',
}

export type EmployeeBaseResourceData = UserData<EmployeeRole> & {
  profilePhoto: string;
  resourceType: CaseResourceType;
  status: string;
  type: UserType;
  employeeId: Uuid;
  labels: EmployeeLabels[] | null;
};

export const NO_COORDINATOR_ID = 'no-coordinator';

export type BaseCaseResource = {
  caseId: string;
  caseResourcesReservationId: string;
  logisticsStatus: ResourceLogisticsStatus;
  resourceId: string;
  resourceUserId: string;
  resourceData?: EmployeeBaseResourceData;
};

export type CaseEmployeeResource = {
  resourceType: CaseResourceType.Employee;
  data?: EmployeeComposed;
};

export type CaseClinicalSpecialistResource = {
  resourceType: CaseResourceType.ClinicalSpecialist;
  data?: EmployeeComposed;
} & BaseCaseResource;

export type CaseRetrievalSurgeonResource = {
  resourceType: CaseResourceType.Surgeon;
  data?: EmployeeComposed;
} & BaseCaseResource;

export type CaseOCSConsoleResource = {
  resourceType: CaseResourceType.OCSConsole;
  data?: OcsConsoleData;
} & BaseCaseResource;

export type CaseAircraftResource = {
  resourceType: CaseResourceType.Aircraft;
  routeIds?: string[];
  data?: AircraftAvailabilityView;
} & BaseCaseResource;

export type CaseResource<T = CaseEmployeeResource> = BaseCaseResource & T;

export type CaseResourceCombined =
  | CaseResource
  | CaseResource<CaseOCSConsoleResource>
  | CaseResource<CaseAircraftResource>
  | CaseResource<CaseClinicalSpecialistResource>
  | CaseResource<CaseRetrievalSurgeonResource>;

export type QuoteComment = {
  userId: Uuid;
  comment: string | null;
};

export enum QuoteRejectReason {
  NotSuitable = 'NotSuitable',
  FullOwnLogistics = 'FullOwnLogistics',
  OwnAirLogistics = 'OwnAirLogistics',
  OwnGroundLogistics = 'OwnGroundLogistics',
}

export type Quote = {
  id: Uuid;
  caseId: string;
  status: QuoteStatus;
  quoteId: string;
  rejectReason: QuoteRejectReason | null;
  comment: QuoteComment | null;
  recallComment: QuoteComment | null;
  sentToTxCenterAt: string;
  excludeDryRunDiscount: boolean | null;
};
export type UserData<TRole> = {
  id: Uuid | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  enabled: boolean;
  roles: TRole[];
  photoPath?: string;
};

export type ResourceUserData<TRole> = UserData<TRole> & Pick<CaseResourceCombined, 'logisticsStatus'>;

export type AssignedUserData = UserData<UserRole> & {
  id: Uuid;
  firstName: string;
  lastName: string;
  photoPath: string;
  email: string;
  phone: string;
  secondaryPhone: string;
  enabled: boolean;
  type: UserType;
  isPrimary?: boolean;
};

export type AssignedUser = {
  userId: Uuid;
  group: CaseUserGroup;
  isPrimary?: boolean;
  isDefaultTxUser?: boolean;
  employeeId?: Uuid;
  customerId?: Uuid;
  user: AssignedUserData;
  isServiceWithoutCoordinator?: boolean;
};

export type CaseFacility = {
  id: Uuid;
  name: string;
  code: string;
  logoPath?: string;
  location: Location;
  hasFixedPriceContract?: boolean | null;
  trialPrograms?: TrialProgram[];
  nonStopFlightAllowed?: boolean;
};

export enum SurgeonCaseSummaryStatus {
  Incomplete = 'Incomplete',
  Completed = 'Completed',
}

export enum LiverQuality {
  NoQualityIssues = 'NoQualityIssues',
  Congested = 'Congested',
  Steatotic = 'Steatotic',
  Cirrhotic = 'Cirrhotic',
  CalcifiedVessels = 'CalcifiedVessels',
  Other = 'Other',
}

export enum FlushType {
  Good = 'Good',
  DidNotFlushWell = 'DidNotDlushWell', // Backend typo
}

export enum LiverOutcome {
  PlacedOnOCSLiver = 'PlacedOnOCSLiver',
  NotPlacedOnOCSLiver = 'NotPlacedOnOCSLiver',
  DCDNotPlacedOnOCSLiver = 'DCDNotPlacedOnOCSLiver',
}

export enum AberrantAnatomyType {
  ReplacedRightOrAccessoryRightHepaticArtery = 'ReplacedRightOrAccessoryRightHepaticArtery',
  ReplacedLeftOrAccessoryLeftHepaticArtery = 'ReplacedLeftOrAccessoryLeftHepaticArtery',
  Other = 'Other',
}

export enum ProcurementEnjuryType {
  ArterialInjury = 'ArterialInjury',
  BiliaryInjury = 'BiliaryInjury',
  PortalVeinInjury = 'PortalVeinInjury',
  ParenchymalInjury = 'ParenchymalInjury',
  Other = 'Other',
}

export enum BacktableInjuryType {
  ArterialInjury = 'ArterialInjury',
  BiliaryInjury = 'BiliaryInjury',
  VenousInjury = 'VenousInjury',
}

export enum NoLiverInjuryType {
  IntestinalPerforationWithContamination = 'IntestinalPerforationWithContamination',
  KidneyInjury = 'KidneyInjury',
  PancreasInjury = 'PancreasInjury',
  Other = 'Other',
}

export enum ImpactOfInjuryType {
  NoImpact = 'NoImpact',
  OrganUsedButInjuryChangedAllocationOrResultedInAlterationToPractice = 'OrganUsedButInjuryChangedAllocationOrResultedInAlterationToPractice',
  OrganDeclined = 'OrganDeclined',
}

export enum AssistanceType {
  TrainedProcurementSurgeon = 'TrainedProcurementSurgeon',
  TrainedSurgicalAssistant = 'TrainedSurgicalAssistant',
  SurgicalResidentOrFellow = 'SurgicalResidentOrFellow',
  UnspecializedSurgicalAssistant = 'UnspecializedSurgicalAssistant',
  None = 'None',
}

export type SurgeonCaseSummaryHeart = {
  isSurgicalInjuryOccurred: boolean;
  isOrganNotUsed: boolean;
  challengeDescription: string;
};

export type SurgeonCaseSummaryLiver = {
  generalCaseDetails?: {
    unosID: string;
    date: DateString;
    dateTimeZone: string;
    donorType: DonorType;
    isDonorExpiredInTime: boolean | null;
    liverQuality: {
      liverQualities: LiverQuality[] | null;
      text: string;
    } | null;
    estimatedSteatosis: number | null;
    flush: FlushType | null;
    liverOutcome: LiverOutcome | null;
    warmIschemiaTime: DateString;
    warmIschemiaTimeTimeZone: string;
  };
  times: {
    skinIncision: DateString;
    skinIncisionTimeZone: string;
    crossClampAndFlush: DateString;
    crossClampAndFlushTimeZone: string;
    hepatectomyCompletion: DateString;
    hepatectomyCompletionTimeZone: string;
    backTableDissectionAndLiverCannulationCompletion: DateString;
    backTableDissectionAndLiverCannulationCompletionTimeZone: string;
  };
  vessels: {
    aberrantArterialAnatomy: boolean;
    aberrantAnatomyTypeData: {
      aberrantAnatomyTypes: AberrantAnatomyType[];
      text: string;
    };
    reconstructionDescription: string;
    mediaFiles: ClinicalMediaFile[];
  };
  procurementInjury: {
    anyProcurementOrBackTableInjuries: boolean;
    procurementInjuryTypeData: {
      procurementInjuries: ProcurementEnjuryType[];
      text: string;
    };
    backtableInjuries: BacktableInjuryType[];
    noLiverInjuryTypeData: {
      noLiverInjuries: NoLiverInjuryType[];
      text: string;
    };
    details: string;
    mediaFiles: ClinicalMediaFile[];
    impactOfInjury: ImpactOfInjuryType;
  };
  kidneyProcurement: {
    isKidneysProcuredForTransplantAfterOpoRequest: boolean;
    opoName: string;
    mediaFiles: ClinicalMediaFile[];
  };
  assistance: {
    assistanceInTheDonor: AssistanceType[];
    backtablePreparationAssistance: AssistanceType[];
  };
  summary: {
    mediaFiles: ClinicalMediaFile[];
    text: string;
  };
};

export type SurgeonCaseSummaryData = { status: SurgeonCaseSummaryStatus } & SurgeonCaseSummaryHeart &
  SurgeonCaseSummaryLiver;

export type SurgeonCaseSummary = {
  id: Uuid;
  caseId: Uuid;
  userId: Uuid;
  createdAt: DateString;
  modifiedAt: DateString;
  summary: SurgeonCaseSummaryData;
};

export enum OrganApprovalStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Declined = 'Declined',
}

export enum OrganOnOCSDeclineReason {
  DonorQuality = 'DonorQuality',
  OrganNotViable = 'OrganNotViable',
  RecipientReasons = 'RecipientReasons',
  LogisticalIssues = 'LogisticalIssues',
  TXCenterAcceptanceCriteria = 'TXCenterAcceptanceCriteria',
  AlternatePreservationMethod = 'AlternatePreservationMethod',
}

export enum QuoteDeclineReason {
  FullOwnLogistics = 'FullOwnLogistics',
  OwnAirLogistics = 'OwnAirLogistics',
  NotSuitable = 'NotSuitable',
  // OwnGroundLogistics = 'OwnGroundLogistics',
}

export enum ArchiveDateFiltersName {
  Service = 'Service',
  Outcome = 'Outcome',
  OffOCS = 'OffOCS',
}

export type OrganApproval = {
  id: Uuid;
  caseId: Uuid;
  requestorUserId: Uuid;
  reviewerUserId: Uuid;
  status: OrganApprovalStatus;
  organType: OrganType;
  declineReason: OrganOnOCSDeclineReason;
  approvedAt: string;
  declinedAt: string;
  createdAt: string;
  modifiedAt: string;
};

export enum ReviewStatus {
  Approved = 'Approved',
  Rejected = 'Rejected',
  WaitingForApproval = 'WaitingForApproval',
  NotApplicable = 'NotApplicable',
}

export enum ReviewType {
  Quote = 'Quote',
  Aviation = 'Aviation',
  Ground = 'Ground',
  TXCenterQuote = 'TXCenterQuote',
  TXCenterService = 'TXCenterService',
}

export enum ReviewerType {
  Employee = 'Employee',
  Customer = 'Customer',
  NotDefined = 'NotDefined', // Server only
}

export enum RandomizationResultType {
  OCS = 'OCS',
  SCS = 'SCS',
  NoRandomization = 'No randomization',
}

export enum TrialQuestion {
  RequestedRecoveryForDenovoTrial = 'RequestedRecoveryForDenovoTrial',
  DenovoColdStaticPreservationConfirmation = 'DenovoColdStaticPreservationConfirmation',
  RequestedRecoveryForEnhanceTrial = 'RequestedRecoveryForEnhanceTrial',
  TypeOfEnhanceTrial = 'TypeOfEnhanceTrial',
  EnhanceColdStaticPreservationConfirmation = 'EnhanceColdStaticPreservationConfirmation',
}

export enum TrialAnswer {
  Yes = 'Yes',
  No = 'No',
  LUNGGuard = 'LUNGGuard',
  BAROGuard = 'BAROGuard',
  StandardColdPreservation = 'StandardColdPreservation',
  Other = 'Other',
  PartA = 'PartA',
  PartB = 'PartB',
  Paragonix = 'Paragonix',
  SherpaPak = 'SherpaPak',
  IceCooler = 'IceCooler',
}

export type TrialAnsweredQuestionModel = {
  question: TrialQuestion;
  answer: TrialAnswer;
  customAnswer?: string | null;
};

export enum RandomizationFilterType {
  OCS = 'OCS',
  SCS = 'SCS',
  NotRandomized = 'NotRandomized',
}

export type Review = {
  id: Uuid;
  status: ReviewStatus;
  type: ReviewType;
  reviewerType: ReviewerType;
  reviewerFirstName: string;
  reviewerLastName: string;
  reviewerLogoUrl: string;
  reviewerUserId: Uuid;
  comment: string | null;
  reviewedAt: string;
  user?: CustomerMT | EmployeeComposed | null;
};

export type DraftCase = Omit<Case, 'opoDetails' | 'txCenterDetails'> & {
  opoDetails: CaseFacility | null;
  txCenterDetails: CaseFacility | null;
};

export type RouteCommentType = {
  comment: string;
  userId: string;
  user?: EmployeeComposed | null;
  createdAt?: string;
};

export enum SyncStatus {
  NotStarted = 'NotStarted',
  Succeed = 'Succeed',
}

export type SyncDataStatuses = {
  clinicalDataSyncStatus: SyncStatus;
  inventorySyncStatus: SyncStatus;
  clinicalResourcesSyncStatus: SyncStatus;
};

export type DonorORData = {
  donorHospitalDate: string | null;
  donorHospitalTime: string | null;
  donorHospitalTimeZone: string | null;
};

export type TxEtaHospitalData = {
  txEtaHospitalDate: string | null;
  txEtaHospitalTime: string | null;
  txEtaHospitalTimeZone: string | null;
};

export type TxCenterORData = {
  recipientHospitalDate: string | null;
  recipientHospitalTime: string | null;
  recipientHospitalTimeZone: string | null;
  recipientHospitalORChangeReason: string | null;
};

export type TxCenterORInitialData = Pick<
  TxCenterORData,
  'recipientHospitalDate' | 'recipientHospitalTime' | 'recipientHospitalTimeZone'
>;

export type ETADelayData = {
  displayDelay?: boolean;
  etaDelayEnd?: DateString | null;
};

export type DelayData = {
  delayDate: string;
  delayTime: string;
  delayTimeZone: string;
  delayNote: string;
};

export type Randomization = {
  randomizationDateTime: DateString;
  timeZone: string;
  txCenterRepresentativeName: string;
  txCenterId: string;
  result: RandomizationResultType;
  isFromReAllocation: boolean;
};

export type KidneyRandomization = {
  randomizationDateTime: DateString;
  timeZone: string;
  representativeName: string;
  txCenterId: Uuid | null;
  opoId: Uuid | null;
  result: RandomizationResultType;
};

type TrialTypes = {
  isRandomizationFormCompleted: boolean;
  isRandomizationRequired: boolean;
  randomization?: Randomization;
  questionnaire?: TrialAnsweredQuestionModel[];
  isQuestionaryEditAllowed: boolean;
  trialProgram?: TrialProgram;
  reallocationData?: ReallocationData | null;
};

type RouteReferenceType = {
  id: Uuid;
  type: RouteTypeEnum;
};

type LogisticsReferenceType = {
  id: Uuid;
  routes: RouteReferenceType[];
};

export type AssignedUsersByGroup = Partial<Record<CaseUserGroup, AssignedUser[]>>;

export type AssignedOpoContactType = Pick<
  OpoOrganizationContact,
  | 'id'
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'isPrimary'
  | 'photoPath'
  | 'primaryPhone'
  | 'secondaryPhone'
  | 'userId'
  | 'roles'
> & {
  isOpoUser: boolean;
  organizations: CaseFacility[];
  parent: CaseFacility;
};

export type CaseData = CaseStatusType & DonorORData & TxEtaHospitalData & TxCenterORData & DelayData & TrialTypes;
export type Case = CaseData & {
  caseId: Uuid;
  benchmarkId?: string;
  caseName: string;
  creationSide: CaseOrigin;
  matchId?: string | null;
  donorData: DonorData | null;
  donorType: DonorType;
  serviceType: ServiceType;
  serviceDetails: string | null | undefined;
  txCenterId: Uuid;
  txCenterDetails: CaseFacility;
  opoSource: string;
  opoId: Uuid | null;
  opoDetails: CaseFacility | null;
  assignedOpoContacts: AssignedOpoContactType[];
  donorHospitalId: Uuid;
  donorHospitalDetails?: CaseFacility;
  donorId: string;
  primaryCoordinatorId: Uuid;
  logisticsCoordinatorId: Uuid;
  notes: CaseNote[];
  invoices: Invoice[];
  routePlanningStatus?: RoutePlanStatus;
  quote?: Quote;
  organType: OrganType;
  assignedEmployees: AssignedUsersByGroup;
  assignedCustomers: AssignedUsersByGroup;
  resources: CaseResourceCombined[];
  opoRequest: boolean;
  isExpeditedCase?: boolean;
  pediatricCase: boolean;
  reAllocationCase: boolean;
  reAllocationCaseId?: Uuid | null;
  reAllocatedToCaseId?: Uuid;
  reAllocationSequenceNumber?: number;
  duplicateCaseNumber?: number;
  logisticProviderTypes: LogisticsProviderType;
  files: CaseFile[];
  links: CaseLinkType[];
  nrpTimer: string;
  isFavourite: boolean;
  createdAt: DateString;
  lastModifiedDate: DateString; // not in API
  organAcceptanceCriteria: OrganAcceptanceCriteriaFields;
  surgeonCaseSummary: SurgeonCaseSummary | null;
  organApproval: OrganApproval | null;
  reviews: Review[];
  clinicalData: ClinicalData;
  isLightsSirensRequired: boolean;
  routeComment: RouteCommentType | null;
  additionalServices: boolean;
  isQuestionaryEditAllowed: boolean;
  txCenterServiceApprovalStatus: ReviewStatus;
  useFixedPriceFlow: boolean;
  caseDataSyncStatuses: SyncDataStatuses;
  clinicalScreeningDateTime?: DateString;
  organOnOCSTimestamp?: DateString | null;
  organOffOCSTimestamp?: DateString | null;
  offOCSData: OffOcsType | null;
  isServiceWithoutCoordinator?: boolean | null;
  serviceRegionId?: Uuid;
  logisticsReference?: LogisticsReferenceType;
  nonStopFlightRequired?: boolean;
  laterality?: Laterality | null;
  splitMetadata?: SplitMetadata | null;
  /** Kidney trial marker; independent of trialProgram. */
  isKidneyTrial?: boolean;
};
export type ReallocationData = Pick<
  Case,
  | 'caseId'
  | 'reAllocationCaseId'
  | 'reAllocationSequenceNumber'
  | 'reAllocatedToCaseId'
  | 'creationSide'
  | 'outcomeData'
  | 'trialProgram'
  | 'questionnaire'
  | 'randomization'
>;

export type ArchivedCase = Pick<
  Case,
  | 'caseId'
  | 'status'
  | 'caseName'
  | 'organType'
  | 'donorType'
  | 'outcomeData'
  | 'caseOutcome'
  | 'createdAt'
  | 'invoices'
  | 'donorHospitalDetails'
  | 'txCenterDetails'
  | 'clinicalScreeningDateTime'
  | 'offOCSData'
  | 'trialProgram'
  | 'randomization'
>;

export type ArchiveCloseCaseResponse = {
  closedCases: Uuid[];
  notClosedCases: Uuid[];
};

export type LogResource = {
  resourceType: CaseResourceType;
  id: Uuid;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  enabled?: boolean;
  type?: UserType;
  roles?: UserRole[];
  profilePhoto?: string;
  resourceName?: string;
  serialNumber?: string;
  tailNumber?: string;
};

export type QuoteLogInfo = {
  isRelatedSumReferable: boolean;
  newQuoteId: Uuid | null;
  relatedToQuoteId: Uuid | null;
};

export type CaseLogLinkedCase = {
  caseId: Uuid;
  caseName: string;
};

export type CaseLogRecord = {
  user?: Partial<LogResource>;
  resource?: LogResource;
  propertyName: string;
  previousValue?: string;
  value: string;
  group?: CaseUserGroup;
  isPrimary?: boolean;
  donorId?: string;
  // CaseResourceLogisticsStatusUpdated
  resourceId?: Uuid;
  resourceType?: CaseResourceType;
  // OrganDeclined
  declineReason?: string;
  quoteInfo?: QuoteLogInfo;
  // CaseInitiatedFromAnotherDraft
  originalCase?: CaseLogLinkedCase;
  // CaseSplitIntoOrgans
  splittedCases?: CaseLogLinkedCase[];
};

// WIP
export type CaseLog = {
  id: string;
  caseId: string;
  eventType: CaseLogEventType;
  createdAt: string;
  user?: {
    userId: string;
    firstName: string;
    lastName: string;
    profilePhoto: string;
    roles: EmployeeRole[] | null;
  };
  userId: string;
  logRecords: Array<CaseLogRecord>;
  quoteTotals?: QuoteTotalType;
};

export type CaseLinkType = {
  internalUuid?: Uuid;
  id: Uuid;
  url: string;
  description: string;
  isNew?: boolean;
};

export type OpoContactIdIsPrimaryPair = {
  opoContactId: Uuid;
  isPrimary: boolean;
  primaryPhone?: string;
};

export type CustomLocation = {
  addressId?: string;
  latitude?: number | null;
  longitude?: number | null;
  country?: string;
  state?: string | null;
  title?: string;
  addressLine?: string;
};

export type Assignee = {
  customerId?: string | null;
  group: string;
  action?: string;
};

export enum Laterality {
  Left = 'Left',
  Right = 'Right',
  NotDefined = 'NotDefined',
}

export type NewCase = {
  caseId: Uuid;
  donorId: string;
  matchId?: string | null;
  donorType: DonorType;
  serviceType: ServiceType;
  organType: OrganType;
  donorHospitalId: Uuid;
  donorHospitalDate?: string | null;
  donorHospitalTime?: string | null;
  donorHospitalTimeZone?: string | null;
  recipientHospitalDate?: string | null;
  recipientHospitalTime?: string | null;
  recipientHospitalTimeZone?: string | null;
  txEtaHospitalDate?: string | null;
  txEtaHospitalTime?: string | null;
  txEtaHospitalTimeZone?: string | null;
  hospitalCustomLocation: CustomLocation | null;
  txCenterCustomLocation?: CustomLocation | null;
  opoId: Uuid | null;
  opoContactId?: Uuid | null;
  secondaryOpoContactId?: Uuid | null;
  txCenterId: Uuid;
  files: TMDXFile[];
  links: CaseLinkType[];
  isExpeditedCase?: boolean;
  opoRequest?: boolean;
  pediatricCase?: boolean;
  reAllocationCase?: boolean;
  reAllocationCaseId?: Uuid | null;
  nrp?: NRPOption;
  nrpType: NRPType;
  additionalServices?: boolean;
  opoContactIdIsPrimaryPairs?: OpoContactIdIsPrimaryPair[];
  assignees?: Assignee[];
  defaultTxAssignees?: Assignee[];
  isInTrial?: boolean;
  questionnaire?: TrialAnsweredQuestionModel[];
  serviceRegionId?: string;
  laterality?: Laterality | null;
};

export type DeclineCaseParams = {
  declineReason: OutcomeType;
  dispositionReason: DispositionReasonType;
  dispositionSpecification: DispositionSpecificationType;
  turndownOutcome?: TurndownOutcome;
  dispositionComment: string;
  timeZone: string;
  timestamp: string;
};

export type CloseCaseParams = {
  caseIds: string[];
};

export type ArchiveCaseFilters = {
  organType?: string;
  donorType?: string;
  caseOutcomes?: string;
};

export type ArchiveFilterConfig = {
  statusList: { value: CaseStatus | string; name: string }[];
  outcomeList: { value: OutcomeType; name: string }[];
};

export type ArchiveCaseListFilters = {
  organType: OrganType[];
  donorType: DonorType[];
  caseStatuses: CaseStatus[];
  outcomeType: OutcomeType[];
  caseOutcome: CaseStatus[];
  txCenterIds: string[];
  opoIds: string[];
  donorHospitalIds: string[];
  serviceRequestDateTimeFrom: DateValue[];
  serviceRequestDateTimeTo: DateValue[];
  outcomeDateTimeFrom: DateValue[];
  outcomeDateTimeTo: DateValue[];
  offOCSDateTimeFrom: DateValue[];
  offOCSDateTimeTo: DateValue[];
  hasInvoice: InvoiceState[];
  invoiceStatuses: InvoiceStatus[];
  customerCaseStatuses?: CustomerCaseStatus[];
  trialProgramIds: string[];
  randomizationResultFilter: RandomizationFilterType[];
  serviceRegionIds: ServiceRegionId[];
  opoRequest?: boolean;
  isReallocatedFromSource?: boolean;
};

export type CasesFilters = {
  searchQuery?: string;
  organType?: string;
  donorType?: string;
  logisticProviderTypes?: string;
  routePlanningStatuses?: string;
  quoteStatuses?: string;
  groundConfirmationStatuses?: string;
  isFavouriteFilter?: boolean;
  isModifiedFilter?: boolean;
  assignedUsers?: string[];
  caseStatuses?: string;
  donorId?: string;
  caseIds?: string; // string[]
  trialProgramIds?: string[];
  randomizationResultFilter?: RandomizationFilterType[];
  serviceRegion?: string;
  opoIds?: string[];
  donorHospitalIds?: string[];
};

export type CustomerCasesFiltersQueryParams = {
  txCenterIds?: string;
  customerCaseStatuses?: string;
};

export type SearchQueryByCaseName = {
  caseName?: string;
};

export type CasesListFilters = {
  organType: string;
  caseStatuses: CaseStatus;
  donorType: string;
  provider: string;
  quick: string;
  assignedUsers: string[];
};

export type PaginatedCaseListFilters = PaginationSearchParams & SearchQuery & UseQueryConfig & CasesFilters;
// Tolerant of unknown/extra keys - the backend may return counts for statuses this app doesn't model (e.g. OPO-only statuses).
export type CaseStatusCounts = Partial<Record<CaseStatus, number>>;
export type PaginatedCustomerCaseListFilters = PaginationSearchParams &
  UseQueryConfig &
  CustomerCasesFiltersQueryParams &
  SortOrderQuery;

export type CaseDuplicateType = {
  caseId: string;
  caseNumber: string | null;
  donorId: string;
  organType: OrganType;
  caseName: string;
  status: CaseStatus;
};

export type ReallocationCandidatesPayload = {
  organType?: OrganType;
  donorType?: DonorType;
  initialReallocationCaseId?: Uuid;
  serviceRegionId: Uuid;
};

export type ReallocationCandidate = {
  caseId: Uuid;
  caseName: string;
};

export type ApiEnvelope<T> = {
  data: T;
  hasError: boolean;
  hasNotError: boolean;
  hasData: boolean;
};

export type CaseRoleOnCase = {
  group: CaseUserGroup;
  isPrimary: boolean;
};

export type CaseAccessTicketUserType = 'Employee' | 'Customer' | string;

export type CaseAccessTicketUser = {
  id: string;
  firstName: string;
  lastName: string;
  photoPath: string;
  email: string | null;
  phone: string | null;
  secondaryPhone: string | null;
  enabled: boolean | null;
  type: CaseAccessTicketUserType | null;
  roles: string[] | null;
  employeeId: string;
  customerId: string | null;
  isSystemUser: boolean;
};

export type CaseAccessTicketStatus = 'Active' | 'NotFound' | 'Revoked' | string;

export type CaseAccessTicketItem = {
  ticketId: string | null;
  caseId: string;
  forUser: CaseAccessTicketUser;
  createdByUser?: CaseAccessTicketUser | null;
  ticketType?: string;
  expiresAt: string | null;
  isInfinite: boolean;
  createdAt?: string | null;
  status: CaseAccessTicketStatus;
  caseRoles: CaseRoleOnCase[] | null;
};

export type GrantAccessBody = {
  forUserId: string;
};

export type CaseAccessTicketsListResponse = CaseAccessTicketItem[];

export type SetAssignedCSLabelBody = {
  labels: EmployeeLabels[];
};

export type {
  AssignedOpoContactLocationType,
  AssignedOpoContactOrganizationType,
  AssignedOpoContactParentType,
} from './assigned-opo-contact.types';
